import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { Cause, Runtime } from "effect";

export const SUPPORT_TICKET_HYDRATION_MAX_FAILURES = 4;
export const SUPPORT_TICKET_HYDRATION_ERROR_REFETCH_INTERVAL_MS = 15_000;

const RETRY_DELAY_MS = 1000;
const RETRY_DELAY_MAX_MS = 8000;

const NON_RETRYABLE_STATUS = new Set([400, 401, 403, 404, 409, 422]);
const RETRYABLE_STATUS = new Set([0, 408, 425, 429, 500, 502, 503, 504]);
const NETWORK_ERROR_PATTERN =
  /failed to fetch|transport error|network request failed|network error|networkerror|load failed/i;

export const SUPPORT_TICKET_HYDRATION_QUERY_CLIENT_DEFAULTS = {
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
};

export function applySupportTicketHydrationQueryClientDefaults(
  queryClient: QueryClient,
  queryKey: QueryKey,
): void {
  queryClient.setQueryDefaults(
    queryKey,
    SUPPORT_TICKET_HYDRATION_QUERY_CLIENT_DEFAULTS,
  );
}

export function shouldRetrySupportTicketHydrationQuery(
  failureCount: number,
  error: unknown,
): boolean {
  if (!isRetryableSupportTicketHydrationError(error)) {
    return false;
  }
  return failureCount < SUPPORT_TICKET_HYDRATION_MAX_FAILURES;
}

export function supportTicketHydrationQueryRetryDelay(
  failureCount: number,
): number {
  return Math.min(RETRY_DELAY_MS * 2 ** failureCount, RETRY_DELAY_MAX_MS);
}

export function supportTicketHydrationQueryRefetchInterval(query: {
  state: { status: string; error: unknown };
}): number | false {
  if (
    query.state.status === "error" &&
    isRetryableSupportTicketHydrationError(query.state.error)
  ) {
    return SUPPORT_TICKET_HYDRATION_ERROR_REFETCH_INTERVAL_MS;
  }
  return false;
}

export const supportTicketHydrationQueryRetryOptions = {
  retry: shouldRetrySupportTicketHydrationQuery,
  retryDelay: supportTicketHydrationQueryRetryDelay,
  refetchInterval: supportTicketHydrationQueryRefetchInterval,
};

export function getCachedQueryRefetchInterval(options: unknown): unknown {
  if (typeof options !== "object" || options === null) {
    return undefined;
  }
  if (!("refetchInterval" in options)) {
    return undefined;
  }
  return options.refetchInterval;
}

export function isRetryableSupportTicketHydrationError(
  error: unknown,
): boolean {
  const candidates = unwrapHydrationErrors(error);
  if (candidates.some(isAbortError)) {
    return false;
  }

  const explicitRetryable = candidates
    .map(getExplicitRetryable)
    .find((value) => value !== undefined);
  if (explicitRetryable === false) {
    return false;
  }
  if (explicitRetryable === true) {
    return true;
  }

  const statuses = candidates
    .map(getStatusCode)
    .filter((status): status is number => status !== undefined);
  if (statuses.some((status) => NON_RETRYABLE_STATUS.has(status))) {
    return false;
  }
  if (statuses.some((status) => RETRYABLE_STATUS.has(status))) {
    return true;
  }

  return candidates.some(isNetworkErrorMessage);
}

function unwrapHydrationErrors(error: unknown): unknown[] {
  if (!Runtime.isFiberFailure(error)) {
    return [error];
  }
  const cause = error[Runtime.FiberFailureCauseId];
  return [...Cause.defects(cause), ...Cause.failures(cause), error];
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AbortError"
  );
}

function getExplicitRetryable(error: unknown): boolean | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "isRetryable" in error &&
    typeof error.isRetryable === "boolean"
  ) {
    return error.isRetryable;
  }
  return undefined;
}

function getStatusCode(error: unknown): number | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "statusCode" in error &&
    typeof error.statusCode === "number"
  ) {
    return error.statusCode;
  }
  return undefined;
}

function isNetworkErrorMessage(error: unknown): boolean {
  if (typeof error === "string") {
    return NETWORK_ERROR_PATTERN.test(error);
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return NETWORK_ERROR_PATTERN.test(error.message);
  }
  return false;
}
